// ===============================================================
// ProBank — Real-time Transaction Toast + Success Chime
//
// Connects to the backend Socket.IO server, joins the logged-in
// user's room, and drops down a success banner with a chime
// whenever a transaction_success event arrives — even if the
// user is on a different page when it happens.
//
// Mounted once in App.js (inside <Layout>) so it works app-wide.
//
// The chime is generated with the Web Audio API — no audio file
// needed, nothing to upload, works on every phone.
// ===============================================================

import React, { useEffect, useState, useRef, useCallback } from "react";
import { io } from "socket.io-client";

const SOCKET_URL = "https://alkebulanfkalertbackend.onrender.com";

// Success chime — two-note bell via Web Audio API
function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;

    const notes = [
      { freq: 880, start: 0, dur: 0.18 },     // A5
      { freq: 1318.5, start: 0.12, dur: 0.3 }, // E6
    ];

    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.25, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    });

    // Close the context after the chime finishes
    setTimeout(() => ctx.close().catch(() => {}), 1000);
  } catch {
    // Audio blocked or unsupported — toast still shows, just silent
  }
}

export default function TransactionToast() {
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const socketRef = useRef(null);
  const hideTimer = useRef(null);

  const showToast = useCallback((payload) => {
    setToast(payload);
    setVisible(true);
    playChime();

    // Update the stored balance so dashboards show it fresh
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      if (payload.newBalance !== undefined) {
        u.balance = payload.newBalance;
        localStorage.setItem("user", JSON.stringify(u));
      }
    } catch {}

    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setVisible(false), 6000);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    let userId = localStorage.getItem("userId");

    if (!userId) {
      try {
        const u = JSON.parse(localStorage.getItem("user") || "{}");
        userId = u._id || u.id || u.userId || null;
      } catch {}
    }

    // Guests and public pages get no socket
    if (!token || !userId) return;

    const socket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("joinUser", String(userId));
    });

    socket.on("transaction_success", showToast);

    return () => {
      clearTimeout(hideTimer.current);
      socket.off("transaction_success", showToast);
      socket.disconnect();
    };
  }, [showToast]);

  if (!toast) return null;

  return (
    <div
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 300,
        display: "flex", justifyContent: "center",
        pointerEvents: "none",
        transform: visible ? "translateY(0)" : "translateY(-110%)",
        transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          pointerEvents: "auto",
          width: "100%", maxWidth: 420,
          background: "#14171c",
          border: "1px solid rgba(34,197,94,0.35)",
          borderRadius: 14,
          padding: "14px 16px",
          display: "flex", alignItems: "center", gap: 12,
          boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
          fontFamily: "'Inter', -apple-system, sans-serif",
        }}
      >
        {/* Success icon */}
        <div style={{
          width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
          background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#22c55e", fontSize: 18, fontWeight: 800,
        }}>
          ✓
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#edeae3", fontSize: 13, fontWeight: 700 }}>
            {toast.title || "Transaction Successful"}
          </div>
          <div style={{ color: "#8b8d93", fontSize: 12, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {toast.description || ""}
          </div>
          {toast.amount !== undefined && (
            <div style={{ color: "#22c55e", fontSize: 13, fontWeight: 700, marginTop: 3 }}>
              {toast.currencySymbol || "₦"}
              {Number(toast.amount).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
            </div>
          )}
        </div>

        <button
          onClick={() => setVisible(false)}
          style={{
            background: "none", border: "none", color: "#8b8d93",
            fontSize: 16, cursor: "pointer", padding: 4, flexShrink: 0,
          }}
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
